import React, { useRef, useEffect } from 'react';
import { Box, Typography, Stack, IconButton, Divider, Fade } from '@mui/material';
import {
    NotificationsNone as NotificationsIcon, Close as CloseIcon,
    CheckOutlined, DeleteOutlineOutlined, FolderOpenOutlined,
    ReceiptLongOutlined, NotificationsActiveOutlined, WifiOutlined
} from '@mui/icons-material';
import { useNotificationStore } from '../../../store/useNotificationStore';

const NOTIF_META = {
    SYSTEM: { icon: WifiOutlined, color: '#4f8ef7', bg: 'rgba(79,142,247,0.12)' },
    FACTURE_PAYEE: { icon: ReceiptLongOutlined, color: '#6dd9a8', bg: 'rgba(109,217,168,0.12)' },
    DOSSIER_CONFIRME: { icon: FolderOpenOutlined, color: '#c9a84c', bg: 'rgba(201,168,76,0.12)' },
    NOTIFICATION: { icon: NotificationsActiveOutlined, color: '#f7844f', bg: 'rgba(247,132,79,0.12)' },
};

function NotifCard({ notif, onMarkRead, onDelete }) {
    const meta = NOTIF_META[notif.type] || NOTIF_META.NOTIFICATION;
    const Icon = meta.icon;

    return (
        <Box sx={{
            px: 2.5, py: 2, position: 'relative', transition: 'background 0.18s ease',
            bgcolor: notif.read ? 'transparent' : 'rgba(255,255,255,0.03)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
            '&:hover .notif-actions': { opacity: 1 },
        }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ width: 36, height: 36, borderRadius: '10px', flexShrink: 0, bgcolor: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon sx={{ fontSize: 17, color: meta.color }} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.4 }}>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: notif.read ? 500 : 700, color: notif.read ? 'rgba(255,255,255,0.6)' : 'white' }}>
                            {notif.title}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "row", gap: 0.5, alignItems: "center" }}>
                            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>{notif.time}</Typography>
                            {!notif.read && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: meta.color, boxShadow: `0 0 0 2px ${meta.color}30` }} />}
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.77rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.45 }}>{notif.message}</Typography>

                    <Stack className="notif-actions" direction="row" spacing={1} sx={{ mt: 1, opacity: 0, transition: 'opacity 0.18s' }}>
                        {!notif.read && (
                            <Typography onClick={() => onMarkRead(notif.id)} sx={{ fontSize: '0.65rem', color: '#c9a84c', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                                Marquer lu
                            </Typography>
                        )}
                        <Typography onClick={() => onDelete(notif.id)} sx={{ fontSize: '0.65rem', color: '#f87171', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                            Supprimer
                        </Typography>
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}

export function NotificationPanel({ anchorEl, open, onClose }) {
    const { notifications, markAsRead, markAllRead, deleteNotif, unreadCount } = useNotificationStore();
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open, onClose, anchorEl]);

    if (!open) return null;

    const anchorRect = anchorEl?.getBoundingClientRect();
    const right = anchorRect ? window.innerWidth - anchorRect.right : 16;
    const top = anchorRect ? anchorRect.bottom + 10 : 60;

    return (
        <Fade in={open} timeout={180}>
            <Box ref={panelRef} sx={{
                position: 'fixed', top, right: Math.max(right - 20, 12), zIndex: 1400, width: 360,
                borderRadius: '10px', overflow: 'hidden', bgcolor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
            }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0d3b4' }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <NotificationsIcon sx={{ fontSize: 16, color: '#c9a84c' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'white' }}>Notifications</Typography>
                                {unreadCount > 0 && <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>{unreadCount} non lue(s)</Typography>}
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            {unreadCount > 0 && <Typography onClick={markAllRead} sx={{ fontSize: '0.68rem', color: '#c9a84c', fontWeight: 700, cursor: 'pointer' }}>Tout lire</Typography>}
                            <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.35)' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ maxHeight: 380, overflowY: 'auto', '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 } }}>
                    {notifications.length === 0 ? (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.3)' }}>Aucune notification</Typography>
                        </Box>
                    ) : (
                        notifications.map((notif, idx) => (
                            <React.Fragment key={notif.id}>
                                <NotifCard notif={notif} onMarkRead={markAsRead} onDelete={deleteNotif} />
                                {idx < notifications.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mx: 2.5 }} />}
                            </React.Fragment>
                        ))
                    )}
                </Box>
            </Box>
        </Fade>
    );
}